import { request, type RequestResponse } from '@atlassian/twg-common';

import type { AvailableSite } from '../../ui/components/SiteSwitcher/types';

/**
 * Single entry in the response from
 * `POST /gateway/api/permissions/bulk/permitted`.
 */
export type BulkPermittedEntry = {
	principalId?: string;
	resourceId: string;
	permissionId: string;
	permitted: boolean;
	dontRequirePrincipalInSite?: boolean;
};

/**
 * Response body of `POST /gateway/api/permissions/bulk/permitted` — an
 * array of one entry per requested `(permissionId, resourceId)` pair.
 */
export type BulkPermittedResponse = BulkPermittedEntry[];

const JSM_SITE_ARI_PREFIX = 'ari:cloud:jira-servicedesk::site/';

/**
 * Build the JSM site ARI used as `resourceId` in the bulk-permitted
 * request body.
 */
const toJsmSiteAri = (cloudId: string): string => `${JSM_SITE_ARI_PREFIX}${cloudId}`;

/**
 * Fire `POST /gateway/api/permissions/bulk/permitted` to check whether
 * the current user has the `write` permission on each of the supplied
 * cloudIds' JSM resources.
 *
 * Used by `availableSitesAtom` to decide whether a JSM-only site (where
 * the user has no admin access) should be hidden from the microsite
 * Site Switcher. See `extractPermittedCloudIds` for parsing the
 * response back into a set of cloudIds.
 *
 * See: https://developer.atlassian.com/platform/identity/public-apis/first-party-public-apis/public-bulk-permitted
 */
export const requestJsmSitePermissions = (
	cloudIds: readonly string[],
): Promise<RequestResponse<BulkPermittedResponse>> => {
	const body = cloudIds.map((cloudId) => ({
		permissionId: 'write',
		resourceId: toJsmSiteAri(cloudId),
	}));
	return request<BulkPermittedResponse>('post', '/gateway/api/permissions/bulk/permitted', body);
};

/**
 * Reduce a `BulkPermittedResponse` to the set of cloudIds for which the
 * user has `permitted === true` on their JSM site resource. Entries
 * with malformed `resourceId` (i.e. not the expected
 * `ari:cloud:jira-servicedesk::site/<cloudId>` shape) and entries with
 * `permitted: false` are silently skipped — callers fold those into
 * "not permitted".
 */
export const extractPermittedCloudIds = (entries: BulkPermittedResponse): Set<string> => {
	const permitted = new Set<string>();
	for (const entry of entries) {
		if (!entry.permitted) {
			continue;
		}
		if (typeof entry.resourceId !== 'string' || !entry.resourceId.startsWith(JSM_SITE_ARI_PREFIX)) {
			continue;
		}
		const cloudId = entry.resourceId.slice(JSM_SITE_ARI_PREFIX.length);
		if (cloudId.length > 0) {
			permitted.add(cloudId);
		}
	}
	return permitted;
};

/**
 * Set of "core productivity" product types whose presence on a site
 * disqualifies it from the JSM-only filter. If a site has any of these
 * (alongside JIRA_SERVICE_DESK), the user has another reason to keep
 * the site in the switcher and we don't permission-check it.
 *
 * Baseline internal products (SEARCH, STUDIO, CHAT, TEAMS, GOALS,
 * PROJECTS, ATLASSIAN_HOME, COMPANY_HUB, ADMINISTRATION) and the
 * deliberately-excluded COMPASS / BITBUCKET / STATUSPAGE are not in
 * this set, so a site with only JSM + any of those still counts as
 * "JSM-only".
 */
const CORE_PRODUCTIVITY_PRODUCTS: ReadonlySet<string> = new Set([
	'CONFLUENCE',
	'JIRA_SOFTWARE',
	'JIRA_BUSINESS',
	'JIRA_PRODUCT_DISCOVERY',
	'JIRA_CUSTOMER_SERVICE',
	'LOOM',
	'ASSETS',
]);

const JSM_PRODUCT_TYPE = 'JIRA_SERVICE_DESK';

/**
 * `true` if the site has JSM in its `availableProducts` and no other
 * core productivity product (see `CORE_PRODUCTIVITY_PRODUCTS`). Such
 * sites are the candidates for the bulk-permitted check in
 * `availableSitesAtom`.
 */
export const isJsmOnlySite = (site: AvailableSite): boolean => {
	const types = site.availableProducts.map((p) => p.productType);
	if (!types.includes(JSM_PRODUCT_TYPE)) {
		return false;
	}
	return !types.some((t) => t !== JSM_PRODUCT_TYPE && CORE_PRODUCTIVITY_PRODUCTS.has(t));
};
